import { useState, useMemo, useRef, useEffect } from 'react';
import eye from '../assets/svg-icons/eye.svg';
import { countryCodes } from './countryCodes';
import { BsFillPatchQuestionFill } from 'react-icons/bs';
import { FiSearch, FiChevronDown } from 'react-icons/fi';

const Input = ({
  label,
  type,
  isPassword,
  placeholder,
  name,
  value,
  onChange,
  onError,
  onBlur,
  onKeyDown,
  disabled,
  countryCodeDisplay,
  isLabelIcon,
  iconTitle,
  ...otherProps
}) => {
  const [inputType, setInputType] = useState(type || 'text');
  useEffect(() => { setInputType(type || 'text'); }, [type]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]); // Default to first country
  const dropdownRef = useRef(null);

  const togglePassword = () => {
    setInputType((prevType) => (prevType === 'password' ? 'text' : 'password'));
  };

  const filteredCountries = useMemo(() => {
    return countryCodes.filter(
      (c) =>
        c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
        c.code.includes(countrySearch),
    );
  }, [countrySearch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div>
      <div className="grid gap-1 text-sm dark:text-white">
        <span className="text-sm flex items-center">
          {label}
          {isLabelIcon && (
            <BsFillPatchQuestionFill
              className="text-sm text-primaryBlue ml-1 cursor-pointer"
              title={iconTitle}
            />
          )}
        </span>
        <div className="relative w-full">
          {isPassword === 'true' && (
            <div
              className="absolute top-0 h-full right-0 p-1 cursor-pointer z-10"
              onClick={togglePassword}
            >
              <span className="aspect-square bg-lightPrimary rounded h-full flex items-center justify-center">
                <img src={eye} alt="" />
              </span>
            </div>
          )}
          <div className="flex lg:flex-row flex-col gap-1">
            {countryCodeDisplay === 'true' && (
              <div className="relative w-full lg:w-32" ref={dropdownRef}>
                <div
                  onClick={() =>
                    !disabled && setIsDropdownOpen(!isDropdownOpen)
                  }
                  className={`flex h-full items-center justify-between cursor-pointer rounded-md border border-[#c4c4c460] bg-white px-3 py-4 text-xs dark:bg-slate-800 ${
                    disabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <span className="truncate">
                    {selectedCountry.code} ({selectedCountry.short_code})
                  </span>
                  <FiChevronDown
                    className={`ml-1 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </div>

                {isDropdownOpen && (
                  <div className="absolute left-0 top-full z-[60] mt-1 w-64 rounded-md border border-[#c4c4c460] bg-white shadow-lg dark:bg-slate-900">
                    <div className="sticky top-0 border-b border-[#c4c4c430] bg-white p-2 dark:bg-slate-900">
                      <div className="relative">
                        <FiSearch className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                        <input
                          autoFocus
                          type="text"
                          placeholder="Search country..."
                          className="w-full rounded border border-[#c4c4c460] py-2 pl-8 pr-2 text-xs focus:outline-0 dark:bg-slate-800"
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <div className="max-h-60 overflow-y-auto py-1">
                      {filteredCountries.length > 0 ? (
                        filteredCountries.map((country, index) => (
                          <div
                            key={index}
                            className="flex cursor-pointer items-center justify-between px-3 py-2 text-xs hover:bg-primaryBlue hover:text-white dark:hover:bg-primaryBlue"
                            onClick={() => {
                              setSelectedCountry(country);
                              setIsDropdownOpen(false);
                              setCountrySearch('');
                            }}
                          >
                            <span className="truncate">{country.name}</span>
                            <span className="text-gray-500 group-hover:text-white/80">
                              {country.code}
                            </span>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-4 text-center text-xs text-gray-500">
                          No results found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <input
              type={inputType}
              placeholder={placeholder}
              name={name}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              onKeyDown={onKeyDown}
              disabled={disabled}
              className="w-full p-4 rounded-md border border-[#c4c4c460] focus:outline-0 placeholder:text-xs dark:bg-slate-800"
              {...otherProps}
            />
          </div>
        </div>
      </div>
      <span className="text-red-500 text-xs">{onError}</span>
    </div>
  );
};

export default Input;
